import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  DialogTitle,
  Divider,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import CustomDialog from "components/reusables/CustomDialog";
import EditIcon from "@mui/icons-material/Edit";
import countryData from "utils/countriesData.json";
import CustomTextInput from "./CustomTextInput";
import CustomDropdown from "./CustomDropdown";
import useRerender from "hooks/useRerender";

const defaultAddress = {
  streetArea: "",
  pincode: "",
  countryId: "",
  countryName: "",
  stateId: "",
  stateName: "",
  cityId: "",
  cityName: "",
};


const AddressInputs = (props) => {
  const { countryData, globalStateRef } = props
  const { address, selected } = globalStateRef
  const reRender = useRerender()

  const clearStateData = () => {
    address.stateName = address.stateId = selected.state = null
    clearCityData()
    clearPincodeData()
  }

  const clearCityData = () => {
    address.cityName = address.cityId = selected.city = null
    clearPincodeData()
  }

  const clearPincodeData = () => {
    address.pincode = ""
  }

  const clearSates = (s) => {
    s === "state"
      ? clearStateData()
      : s === "city"
        ? clearCityData()
        : s === "pinCode"
          ? clearPincodeData()
          : null
  }



  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <CustomTextInput
          label="Street/Area"
          value={address.streetArea}
          onChange={(e) => {
            const newValue = e?.target?.value
            address.streetArea = e?.target?.value
            reRender()
          }}
          isLabel={true}
        />
      </Grid>

      <Grid item xs={6}>
        <CustomDropdown
          value={selected.country}
          label="Country"
          onChange={(e) => {
            address.countryName = e?.name
            address.countryId = e?.countryId
            selected.country = e
            clearSates("state")
            reRender()
          }}
          options={countryData}
          renderOptionKey={"name"}
          isLabel={true}
        />
      </Grid>

      <Grid item xs={6}>
        <CustomDropdown
          value={selected?.state}
          label="State"
          onChange={(e) => {
            address.stateName = e?.stateName
            address.stateId = e?.stateId
            selected.state = e
            clearSates("city")
            reRender()
          }}
          isLabel={true}
          options={selected?.country?.states}
          renderOptionKey={"stateName"}
        />
      </Grid>

      <Grid item xs={6}>
        <CustomDropdown
          value={selected?.city}
          label="City"
          onChange={(e) => {
            address.cityName = e?.cityName
            address.cityId = e?.cityId
            selected.city = e
            clearSates("pinCode")
            reRender()
          }}
          isLabel={true}
          options={selected?.state?.cities}
          renderOptionKey={"cityName"}
        />
      </Grid>

      <Grid item xs={6}>
        <CustomTextInput
          label="Pincode"
          value={address?.pincode}
          onChange={(e) => {
            address.pincode = e?.target?.value
            reRender()
          }}
          isLabel={true}
        />
      </Grid>
    </Grid>
  )
}

const CustomAddress = (props) => {
  const {
    value = {},
    onChange,
    isLock = false,
    isReadOnly = false,
    disabled = false,
    action = "edit",
  } = props;
  const [open, setOpen] = useState(false);

  const globalStateRef = useRef({
    address: defaultAddress,
    selected: {
      country: null,
      state: null,
      city: null,
    }
  })

  const { address } = globalStateRef?.current

  useEffect(() => {
    if (open) {
      if (action === "edit" && value && Object.keys(value)?.length) {
        globalStateRef.current.address = value
        const selectedCountry =
          countryData?.find((country) => (
            (country?.countryId === value?.countryId) && (country?.name === value?.countryName)
          ))

        const selectedState =
          selectedCountry?.states?.find((state) => (
            (state?.stateId === value?.stateId) && (state?.stateName === value?.stateName)
          ))

        const selectedCity = selectedState?.cities?.find((state) => (
          (state?.cityId === value?.cityId) && (state?.cityName === value?.cityName)
        ))

        globalStateRef.current.selected.country = selectedCountry
        globalStateRef.current.selected.state = selectedState
        globalStateRef.current.selected.city = selectedCity


      } else {
        globalStateRef.current.address = defaultAddress
        globalStateRef.current.selected = {
          country: null,
          state: null,
          city: null,
        }
      }
    }
  }, [open]); // ✅ only re-run when dialog opens

  const handleDialogToggle = () => {
    setOpen(!open);
  };

  const handleSave = () => {
    const finalData = {
      ...address,
      finalAddress: [
        address?.streetArea,
        address?.cityName,
        address?.stateName,
        address?.countryName,
        address?.pincode,
      ]
        .filter(Boolean)
        .join(", "),
    };
    if (onChange) onChange(finalData);
    globalStateRef.current.address = defaultAddress
    globalStateRef.current.selected = {
      country: null,
      state: null,
      city: null,
    }
    setOpen(false);
  };

  return (
    <>
      {action === "add" ? (
        <Button
          variant="outlined"
          onClick={handleDialogToggle}
          disabled={disabled || isLock || isReadOnly}
        >
          Add Address
        </Button>
      ) : (
        <Tooltip title="Edit Address">
          <IconButton
            onClick={handleDialogToggle}
            disabled={disabled || isLock || isReadOnly}
            color="success"
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}

      <CustomDialog open={open} onClose={handleDialogToggle} maxWidth="sm">
        <DialogTitle fontWeight="bold">Fill the Address</DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ p: 2 }}>

            <AddressInputs globalStateRef={globalStateRef.current} countryData={countryData} />

          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </CustomDialog>
    </>
  );
};

export default CustomAddress;
